import React, { useCallback, useState } from 'react';
import { useDropzone } from 'react-dropzone';
import { Field, useForm } from 'react-final-form';
import { useDispatch } from 'react-redux';
import { uploadImage } from '../../../../ducks/global.duck';
import css from './UploadImagesField.module.css';
import { FormattedMessage } from 'react-intl';
import { Icons } from '../../../../components';
import { showErrorToast } from '../../../../util/toast';

const UploadImagesField = ({ fieldName = 'uploadedImages', label }) => {
  const dispatch = useDispatch();
  const form = useForm();
  const [uploading, setUploading] = useState(false);

  const onDrop = useCallback(
    async acceptedFiles => {
      if (acceptedFiles.length === 0) return;

      setUploading(true);

      // Get current value using form.getFieldState
      const fieldState = form.getFieldState(fieldName);
      const currentValue = fieldState?.value || [];

      // Create blob URLs for immediate preview
      const blobUrls = acceptedFiles.map(file => {
        const blobUrl = URL.createObjectURL(file);
        return blobUrl;
      });

      // Update field with blob URLs immediately using form.change
      const previewValue = [...currentValue, ...blobUrls];
      form.change(fieldName, previewValue);

      try {
        // Upload each file and collect real URLs
        const realUrls = [];

        for (let i = 0; i < acceptedFiles.length; i++) {
          const file = acceptedFiles[i];

          try {
            const response = await dispatch(uploadImage({ image: file }));

            const realUrl = response.data.attributes.variants.default.url;
            realUrls.push(realUrl);

            // Clean up blob URL
            URL.revokeObjectURL(blobUrls[i]);
          } catch (error) {
            realUrls.push(blobUrls[i]); // Keep blob on failure
          }
        }

        // Get the current value again (in case it changed)
        const latestFieldState = form.getFieldState(fieldName);
        const latestValue = latestFieldState?.value || [];

        // Update field with real URLs
        const finalValue = [...currentValue, ...realUrls];
        form.change(fieldName, finalValue);

        // Force a re-render by also calling form.batch
        form.batch(() => {
          form.change(fieldName, finalValue);
        });
      } catch (error) {
        showErrorToast('Some images failed to upload.');
      } finally {
        setUploading(false);
      }
    },
    [dispatch, form, fieldName]
  );

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: {
      'image/jpeg': ['.jpg', '.jpeg'],
      'image/png': ['.png'],
      'image/gif': ['.gif'],
    },
    multiple: true,
  });

  return (
    <Field name={fieldName}>
      {({ input }) => {
        const value = input.value || [];

        const removeImage = urlToRemove => {
          if (urlToRemove.startsWith('blob:')) {
            URL.revokeObjectURL(urlToRemove);
          }

          const newValue = value.filter(url => url !== urlToRemove);
          form.change(fieldName, newValue);
        };

        return (
          <div className={css.root}>
            <label>{label}</label>
            {/* Dropzone */}
            <div
              {...getRootProps()}
              className={`${css.dropzone} ${isDragActive ? css.dragActive : ''} ${
                uploading ? css.uploading : ''
              }`}
            >
              <input {...getInputProps()} />
              {uploading ? (
                <p>Uploading...</p>
              ) : isDragActive ? (
                <p>Drop images here...</p>
              ) : (
                <>
                  <div className={css.uploadCloud}>
                    <Icons name="upload" />
                  </div>
                  <p>
                    <strong>
                      <FormattedMessage id="UploadImagesField.title" />{' '}
                    </strong>
                    <br />
                    <FormattedMessage id="UploadImagesField.supported" />{' '}
                  </p>
                </>
              )}
            </div>
            {/* Preview Grid */}
            {value.length > 0 && (
              <div className={css.previewGrid}>
                {value.map((imageUrl, index) => {
                  const isBlob = imageUrl.startsWith('blob:');
                  return (
                    <div
                      key={`img-${index}-${imageUrl.substring(0, 20)}`}
                      className={css.imageWrapper}
                    >
                      <img
                        src={imageUrl}
                        alt={`Preview ${index + 1}`}
                        className={css.previewImage}
                        onLoad={() => console.log('Image loaded:', imageUrl)}
                        onError={e => {
                          console.error('Image load error:', imageUrl);
                        }}
                      />
                      {uploading && isBlob && (
                        <div className={css.uploadingOverlay}>
                          <span>Uploading...</span>
                        </div>
                      )}
                      <button
                        type="button"
                        onClick={() => removeImage(imageUrl)}
                        className={css.removeButton}
                      >
                        <Icons name="cross" />
                      </button>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        );
      }}
    </Field>
  );
};

export default UploadImagesField;
